import React, { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { CircularProgress } from "@material-ui/core";
import { notification } from "antd";
import Select from "react-select";
import { Strings } from "../../../../../dataProvider/localize";
import { getSwmsApproverManagers } from "../../../../../actions/v2/SwmsActivityActions";
import { sendSwmsDraftForApprovalV2 } from "../../../../../actions/v2/JobSwmsTaskActions";
import styles from "./SwmsSendApprovalModal.module.css";

/* ── react-select styles matching HTML design (indigo focus, white bg, rounded-xl) ── */
const approverSelectStyles = {
  control: (base, { isFocused, isDisabled }) => ({
    ...base,
    background: isDisabled ? "#F9FAFB" : "#fff",
    border: isFocused ? "1px solid #818CF8" : "1px solid #D1D5DB",
    borderRadius: "10px",
    boxShadow: isFocused ? "0 0 0 3px rgba(129,140,248,0.15)" : "none",
    fontSize: "13px",
    minHeight: "40px",
    cursor: isDisabled ? "not-allowed" : "pointer",
    "&:hover": { borderColor: "#818CF8" },
  }),
  valueContainer: (base) => ({ ...base, padding: "2px 12px" }),
  singleValue: (base) => ({ ...base, color: "#1F2937", fontSize: "13px" }),
  placeholder: (base) => ({ ...base, color: "#9CA3AF", fontSize: "13px" }),
  indicatorSeparator: () => ({ display: "none" }),
  dropdownIndicator: (base, { isDisabled }) => ({
    ...base,
    color: isDisabled ? "#D1D5DB" : "#6B7280",
    padding: "0 10px",
    "&:hover": { color: "#6366F1" },
  }),
  menu: (base) => ({
    ...base,
    borderRadius: "10px",
    boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
    border: "1px solid #E5E7EB",
    overflow: "hidden",
    fontSize: "13px",
    fontFamily: "Poppins, sans-serif",
    zIndex: 9999,
  }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  option: (base, { isFocused, isSelected }) => ({
    ...base,
    background: isSelected ? "#EEF2FF" : isFocused ? "#F5F3FF" : "#fff",
    color: isSelected ? "#4338CA" : "#1F2937",
    fontSize: "13px",
    fontFamily: "Poppins, sans-serif",
    padding: "9px 14px",
    cursor: "pointer",
  }),
};

/* ── Inline SVG icons ── */
const InfoIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" fill="currentColor" className={styles.infoBannerIcon}>
    <path d="M256 512A256 256 0 1 0 256 0a256 256 0 1 0 0 512zM216 336h24V272H216c-13.3 0-24-10.7-24-24s10.7-24 24-24h48c13.3 0 24 10.7 24 24v88h8c13.3 0 24 10.7 24 24s-10.7 24-24 24H216c-13.3 0-24-10.7-24-24s10.7-24 24-24zm40-208a32 32 0 1 1 0 64 32 32 0 1 1 0-64z" />
  </svg>
);

const CloseIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512" fill="currentColor" className={styles.closeBtnIcon}>
    <path d="M342.6 150.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L192 210.7 86.6 105.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3L146.7 256 41.4 361.4c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0L192 301.3l105.4 105.3c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L237.3 256l105.3-105.4z" />
  </svg>
);

const UserPlusIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 512" fill="currentColor" className={styles.externalToggleIcon}>
    <path d="M96 128a128 128 0 1 1 256 0A128 128 0 1 1 96 128zM0 482.3C0 383.8 79.8 304 178.3 304h91.4C368.2 304 448 383.8 448 482.3c0 16.4-13.3 29.7-29.7 29.7H29.7C13.3 512 0 498.7 0 482.3zM504 312V248H440c-13.3 0-24-10.7-24-24s10.7-24 24-24h64V136c0-13.3 10.7-24 24-24s24 10.7 24 24v64h64c13.3 0 24 10.7 24 24s-10.7 24-24 24H552v64c0 13.3-10.7 24-24 24s-24-10.7-24-24z" />
  </svg>
);

const ChevronDownIcon = ({ open }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" fill="currentColor" className={`${styles.externalChevron} ${open ? styles.open : ""}`}>
    <path d="M233.4 406.6c12.5 12.5 32.8 12.5 45.3 0l192-192c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L256 338.7 86.6 169.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3l192 192z" />
  </svg>
);

const PlusIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512" fill="currentColor" className={styles.externalAddIcon}>
    <path d="M256 80c0-17.7-14.3-32-32-32s-32 14.3-32 32V224H48c-17.7 0-32 14.3-32 32s14.3 32 32 32H192V432c0 17.7 14.3 32 32 32s32-14.3 32-32V288H400c17.7 0 32-14.3 32-32s-14.3-32-32-32H256V80z" />
  </svg>
);

const XSmallIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512" fill="currentColor" className={styles.externalChipRemoveIcon}>
    <path d="M342.6 150.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L192 210.7 86.6 105.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3L146.7 256 41.4 361.4c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0L192 301.3l105.4 105.3c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L237.3 256l105.3-105.4z" />
  </svg>
);

const WarningIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" fill="currentColor" className={styles.validationErrorIcon}>
    <path d="M256 32c14.2 0 27.3 7.5 34.5 19.8l216 368c7.3 12.4 7.3 27.7 .2 40.1S486.3 480 472 480H40c-14.3 0-27.6-7.7-34.7-20.1s-7-27.8 .2-40.1l216-368C228.7 39.5 241.8 32 256 32zm0 128c-13.3 0-24 10.7-24 24V296c0 13.3 10.7 24 24 24s24-10.7 24-24V184c0-13.3-10.7-24-24-24zm32 224a32 32 0 1 0 -64 0 32 32 0 1 0 64 0z" />
  </svg>
);

const PaperPlaneIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 576 512" fill="currentColor" className={styles.sendBtnIcon}>
    <path d="M536.4-26.3c9.8-3.5 20.6-1 28 6.3s9.8 18.2 6.3 28l-178 496.9c-5 13.9-18.1 23.1-32.8 23.1-14.2 0-27-8.6-32.3-21.7l-64.2-158c-4.5-11-2.5-23.6 5.2-32.6l94.5-112.4c5.1-6.1 4.7-15-.9-20.6s-14.6-6-20.6-.9L229.2 276.1c-9.1 7.6-21.6 9.6-32.6 5.2L38.1 216.8c-13.1-5.3-21.7-18.1-21.7-32.3 0-14.7 9.2-27.8 23.1-32.8l496.9-178z" />
  </svg>
);

/* ── helpers ── */
const getInitials = (name) =>
  name
    ? name
        .split(" ")
        .map((w) => w[0])
        .join("")
        .slice(0, 2)
        .toUpperCase()
    : "?";

const mapManagerToReviewer = (manager) => {
  if (!manager || typeof manager !== "object") {
    return null;
  }

  const userId = manager.user_name ? manager.user_name : null;
  const email = manager.email_address ? manager.email_address : null;
  const name = manager.first_name ? manager.first_name : null;

  return {
    userId: userId ? `${userId}` : null,
    email: email ? `${email}` : null,
    name: name ? `${name}` : null,
  };
};

export default function SwmsSendApprovalModal({ swms, jobId, onClose, onSuccess }) {
  const dispatch = useDispatch();
  const controller = useRef();
  const formNumber = swms && swms.swms_number ? swms.swms_number : "-";

  /* ── state ── */
  const [loadingManagers, setLoadingManagers] = useState(false);
  const [managers, setManagers] = useState([]);

  // react-select value and selected manager list
  const [dropdownValue, setDropdownValue] = useState(null);
  const [selectedManagers, setSelectedManagers] = useState([]); // array of manager objects

  // optional recipients (won't block approval)
  const [optionalDropdownValue, setOptionalDropdownValue] = useState(null);
  const [selectedOptional, setSelectedOptional] = useState([]); // array of manager objects

  // external reviewers
  const [showExternalForm, setShowExternalForm] = useState(false);
  const [extName, setExtName] = useState("");
  const [extEmail, setExtEmail] = useState("");
  const [extRole, setExtRole] = useState("");
  const [extError, setExtError] = useState(false);
  const [externalReviewers, setExternalReviewers] = useState([]);

  // note
  const [note, setNote] = useState("");

  // submit
  const [submitting, setSubmitting] = useState(false);
  const [showValidationError, setShowValidationError] = useState(false);

  /* ── fetch managers ── */
  useEffect(() => {
    controller.current = new AbortController();
    setLoadingManagers(true);
    dispatch(getSwmsApproverManagers({ signal: controller.current.signal }))
      .then((res) => {
        setManagers(Array.isArray(res) ? res : []);
        setLoadingManagers(false);
      })
      .catch((err) => {
        setLoadingManagers(false);
        if (err) {
          notification.error({
            message: Strings.error_title,
            description: err || Strings.generic_error,
            className: "ant-error",
          });
        }
      });

    return () => {
      if (controller.current) controller.current.abort();
    };
  }, [dispatch]);

  /* ── add manager from dropdown ── */
  const handleAddManager = () => {
    if (!dropdownValue) return;
    const mgr = managers.find((m) => m.user_name === dropdownValue.value);
    if (!mgr) return;
    if (selectedManagers.some((m) => m.user_name === mgr.user_name)) {
      setDropdownValue(null);
      return;
    }
    setSelectedManagers((prev) => [...prev, mgr]);
    setDropdownValue(null);
    setShowValidationError(false);
  };

  const handleRemoveManager = (userName) => {
    setSelectedManagers((prev) => prev.filter((m) => m.user_name !== userName));
  };

  /* ── add optional from dropdown ── */
  const handleAddOptional = () => {
    if (!optionalDropdownValue) return;
    const mgr = managers.find((m) => m.user_name === optionalDropdownValue.value);
    if (!mgr) return;
    if (selectedOptional.some((m) => m.user_name === mgr.user_name)) {
      setOptionalDropdownValue(null);
      return;
    }
    setSelectedOptional((prev) => [...prev, mgr]);
    setOptionalDropdownValue(null);
  };

  const handleRemoveOptional = (userName) => {
    setSelectedOptional((prev) => prev.filter((m) => m.user_name !== userName));
  };

  /* ── external reviewer ── */
  const handleAddExternal = () => {
    const name = extName.trim();
    const email = extEmail.trim();
    if (!name || !email.includes("@")) {
      setExtError(true);
      return;
    }
    setExtError(false);
    setExternalReviewers((prev) => [...prev, { name, email, role: extRole.trim() }]);
    setExtName("");
    setExtEmail("");
    setExtRole("");
    setShowValidationError(false);
  };

  const handleRemoveExternal = (index) => {
    setExternalReviewers((prev) => prev.filter((_, i) => i !== index));
  };

  /* ── submit ── */
  const handleSend = () => {
    if (selectedManagers.length === 0 && externalReviewers.length === 0) {
      setShowValidationError(true);
      return;
    }
    setShowValidationError(false);
    setSubmitting(true);

    const mandatoryReviewers = selectedManagers
      .map(mapManagerToReviewer)
      .filter(Boolean);

    const optionalReviewers = selectedOptional
      .map(mapManagerToReviewer)
      .filter(Boolean);

    const externalReviewerPayload = externalReviewers
      .map((reviewer) => ({
        email: reviewer && reviewer.email ? `${reviewer.email}` : "",
        name: reviewer && reviewer.name ? `${reviewer.name}` : "",
        role: reviewer && reviewer.role ? `${reviewer.role}` : "",
      }))
      .filter((reviewer) => reviewer.email && reviewer.name);

    const formData = {
      mandatoryReviewers,
      optionalReviewers,
      externalReviewers: externalReviewerPayload,
      noteToReviewers: note.trim() || "",
    };

    dispatch(sendSwmsDraftForApprovalV2(jobId, swms.swms_form_id, formData))
      .then((message) => {
        setSubmitting(false);
        notification.success({
          message: Strings.success_title,
          description: message || "SWMS sent for approval.",
          className: "ant-success",
        });
        if (onSuccess) onSuccess();
        onClose();
      })
      .catch((err) => {
        setSubmitting(false);
        if (err) {
          notification.error({
            message: Strings.error_title,
            description: err || Strings.generic_error,
            className: "ant-error",
          });
        }
      });
  };

  /* ── backdrop click ── */
  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget && !submitting) onClose();
  };

  const totalSelected = selectedManagers.length + selectedOptional.length + externalReviewers.length;

  /* managers not yet picked in either mandatory or optional */
  const availableForMandatory = managers.filter(
    (m) => !selectedManagers.some((s) => s.user_name === m.user_name) &&
            !selectedOptional.some((s) => s.user_name === m.user_name)
  );
  const availableForOptional = managers.filter(
    (m) => !selectedOptional.some((s) => s.user_name === m.user_name) &&
            !selectedManagers.some((s) => s.user_name === m.user_name)
  );

  return (
    <div className={styles.overlay}>
      <div className={styles.modal}>

        {/* ── Header ── */}
        <div className={styles.header}>
          <div className={styles.headerText}>
            <h3>Send for Approval</h3>
            <p className={styles.headerSubtitle} style={{ marginBottom: "0px" }}>
              {swms.swms_form_name} · {formNumber}
            </p>
          </div>
          <button
            className={styles.closeBtn}
            onClick={onClose}
            disabled={submitting}
            type="button"
            aria-label="Close"
          >
            <CloseIcon />
          </button>
        </div>

        {/* ── Body ── */}
        <div className={styles.body}>

          {/* Info banner */}
          <div className={styles.infoBanner}>
            <InfoIcon />
            <span>
              Each person will receive an email with a secure link to review and sign or reject with notes.
            </span>
          </div>

          {/* Mandatory approvers */}
          <div>
            <div className={styles.approverSectionHeader}>
              <span className={styles.approverSectionTitle}>Mandatory</span>
              <span className={styles.approverSectionBadge + " " + styles.mandatory}>Must sign to approve</span>
            </div>
            <p className={styles.approverSectionDesc}>These reviewers must complete their review before the SWMS can be approved.</p>
            <div className={styles.dropdownRow}>
              <div className={styles.dropdownWrap}>
                <Select
                  styles={approverSelectStyles}
                  menuPortalTarget={document.body}
                  placeholder={loadingManagers ? "Loading managers…" : "Select a manager"}
                  isLoading={loadingManagers}
                  isDisabled={submitting || loadingManagers}
                  value={dropdownValue}
                  onChange={(v) => setDropdownValue(v)}
                  options={availableForMandatory.map((mgr) => ({
                    value: mgr.user_name,
                    label: mgr.role_name ? `${mgr.first_name} — ${mgr.role_name}` : mgr.first_name,
                    mgr,
                  }))}
                  isClearable
                  hideSelectedOptions
                />
              </div>
              <button
                type="button"
                className={styles.dropdownAddBtn}
                onClick={handleAddManager}
                disabled={!dropdownValue || submitting}
              >
                <PlusIcon />
                Add
              </button>
            </div>

            {selectedManagers.length > 0 && (
              <div className={styles.selectedManagersList}>
                {selectedManagers.map((mgr) => (
                  <div key={mgr.user_name} className={styles.selectedManagerChip}>
                    <div className={styles.selectedManagerAvatar}>
                      {getInitials(mgr.first_name)}
                    </div>
                    <div className={styles.selectedManagerInfo}>
                      <span className={styles.selectedManagerName}>{mgr.first_name}</span>
                      {mgr.role_name && (
                        <span className={styles.selectedManagerRole}>{mgr.role_name}</span>
                      )}
                    </div>
                    <button
                      type="button"
                      className={styles.externalChipRemove}
                      onClick={() => handleRemoveManager(mgr.user_name)}
                      disabled={submitting}
                      aria-label="Remove"
                    >
                      <XSmallIcon />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Optional recipients */}
          <div>
            <div className={styles.approverSectionHeader}>
              <span className={styles.approverSectionTitle}>Optional</span>
              <span className={styles.approverSectionBadge + " " + styles.optional}>Won’t block approval</span>
            </div>
            <p className={styles.approverSectionDesc}>These recipients are notified for visibility. They don’t need to sign and won’t hold up the approval process.</p>
            <div className={styles.dropdownRow}>
              <div className={styles.dropdownWrap}>
                <Select
                  styles={approverSelectStyles}
                  menuPortalTarget={document.body}
                  placeholder={loadingManagers ? "Loading managers…" : "Select a staff member"}
                  isLoading={loadingManagers}
                  isDisabled={submitting || loadingManagers}
                  value={optionalDropdownValue}
                  onChange={(v) => setOptionalDropdownValue(v)}
                  options={availableForOptional.map((mgr) => ({
                    value: mgr.user_name,
                    label: mgr.role_name ? `${mgr.first_name} — ${mgr.role_name}` : mgr.first_name,
                    mgr,
                  }))}
                  isClearable
                  hideSelectedOptions
                />
              </div>
              <button
                type="button"
                className={styles.dropdownAddBtn}
                onClick={handleAddOptional}
                disabled={!optionalDropdownValue || submitting}
              >
                <PlusIcon />
                Add
              </button>
            </div>

            {selectedOptional.length > 0 && (
              <div className={styles.selectedManagersList}>
                {selectedOptional.map((mgr) => (
                  <div key={mgr.user_name} className={`${styles.selectedManagerChip} ${styles.selectedOptionalChip}`}>
                    <div className={`${styles.selectedManagerAvatar} ${styles.selectedOptionalAvatar}`}>
                      {getInitials(mgr.first_name)}
                    </div>
                    <div className={styles.selectedManagerInfo}>
                      <span className={styles.selectedManagerName}>{mgr.first_name}</span>
                      {mgr.role_name && (
                        <span className={styles.selectedManagerRole}>{mgr.role_name}</span>
                      )}
                    </div>
                    <button
                      type="button"
                      className={styles.externalChipRemove}
                      onClick={() => handleRemoveOptional(mgr.user_name)}
                      disabled={submitting}
                      aria-label="Remove"
                    >
                      <XSmallIcon />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Add external reviewer */}
          <div>
            <button
              className={styles.externalToggle}
              type="button"
              onClick={() => setShowExternalForm((v) => !v)}
            >
              <span className={styles.externalToggleLeft}>
                <UserPlusIcon />
                Add external reviewer
              </span>
              <ChevronDownIcon open={showExternalForm} />
            </button>

            {showExternalForm && (
              <div className={styles.externalForm}>
                <p className={styles.externalFormNote}>
                  For people outside your organisation — they'll receive a secure review link. No login required.
                </p>
                <div className={styles.externalFormGrid}>
                  <input
                    type="text"
                    className={styles.externalFormInput}
                    placeholder="Full name *"
                    value={extName}
                    onChange={(e) => { setExtName(e.target.value); setExtError(false); }}
                    disabled={submitting}
                  />
                  <input
                    type="email"
                    className={styles.externalFormInput}
                    placeholder="Email address *"
                    value={extEmail}
                    onChange={(e) => { setExtEmail(e.target.value); setExtError(false); }}
                    disabled={submitting}
                  />
                </div>
                <input
                  type="text"
                  className={styles.externalFormInput}
                  placeholder="Role / Company (optional)"
                  value={extRole}
                  onChange={(e) => setExtRole(e.target.value)}
                  disabled={submitting}
                />
                <button
                  type="button"
                  className={styles.externalAddBtn}
                  onClick={handleAddExternal}
                  disabled={submitting}
                >
                  <PlusIcon />
                  Add External Reviewer
                </button>
                {extError && (
                  <div className={styles.externalError}>
                    <WarningIcon />
                    Please enter a name and a valid email address.
                  </div>
                )}
              </div>
            )}
          </div>

          {/* External chips */}
          {externalReviewers.length > 0 && (
            <div className={styles.externalChipsList}>
              <p className={styles.externalChipsLabel}>External reviewers added</p>
              {externalReviewers.map((r, i) => (
                <div key={i} className={styles.externalChip}>
                  <span className={styles.externalChipName}>{r.name}</span>
                  <span className={styles.externalChipRole}>{r.role || r.email}</span>
                  <button
                    type="button"
                    className={styles.externalChipRemove}
                    onClick={() => handleRemoveExternal(i)}
                    disabled={submitting}
                    aria-label="Remove"
                  >
                    <XSmallIcon />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Note */}
          <div>
            <label className={styles.noteLabel}>
              Note to reviewers{" "}
              <span className={styles.noteOptional}>(optional)</span>
            </label>
            <textarea
              className={styles.noteTextarea}
              rows={2}
              placeholder="Add context or specific items for reviewers to check…"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              disabled={submitting}
            />
          </div>

          {/* Validation error */}
          {showValidationError && (
            <div className={styles.validationError}>
              <WarningIcon />
              Select at least one approver before sending.
            </div>
          )}
        </div>

        {/* ── Footer ── */}
        <div className={styles.footer}>
          <button
            type="button"
            className={styles.cancelBtn}
            onClick={onClose}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="button"
            className={styles.sendBtn}
            onClick={handleSend}
            disabled={submitting || loadingManagers}
          >
            {submitting ? (
              <CircularProgress size={16} style={{ color: "#fff" }} />
            ) : (
              <PaperPlaneIcon />
            )}
            {submitting ? "Sending…" : `Send for Approval${totalSelected > 0 ? ` (${totalSelected})` : ""}`}
          </button>
        </div>
      </div>
    </div>
  );
}
